/**
 * The site visit's confirmation — `docs/new-request.md` §12, T21.
 *
 * Which way it goes (`confirmChannel`) and what it says (`confirmationText`,
 * `confirmationEmail`) are one function each, run by the sheet for the
 * preview and by the server for the message it queues, so the words a person
 * reads before pressing Create are the words the customer receives.
 *
 * The words are fixed until Settings → Notification templates exists (§12.2).
 */
import { betweenWords, clock, dayComma, dayLong, minutesWords, weekdayLong } from './words.js';
/**
 * *Text <first name> a confirmation* when they prefer Text or Either and have a
 * mobile; *Email …* when they prefer Email or have no mobile; nothing when
 * there is nothing to send to. A STOP, or a company with no text number,
 * sends email instead.
 */
export function confirmChannel(input) {
    const mobile = input.mobile?.trim() || null;
    const email = input.email?.trim() || null;
    if (!mobile && !email)
        return { channel: null, to: null, band: 'no_recipient' };
    const wantsText = input.prefers !== 'email';
    if (wantsText && mobile) {
        if (input.optedOut) {
            return email ? { channel: 'email', to: email, band: 'opted_out' }
                : { channel: null, to: null, band: 'opted_out' };
        }
        if (!input.smsReady) {
            return email ? { channel: 'email', to: email, band: 'no_sms_number' }
                : { channel: null, to: null, band: 'no_sms_number' };
        }
        return { channel: 'sms', to: mobile, band: null };
    }
    if (email) {
        return { channel: 'email', to: email, band: input.prefers === 'sms' && !mobile ? 'no_mobile' : null };
    }
    /* Prefers Email, has only a mobile. */
    if (input.optedOut)
        return { channel: null, to: null, band: 'opted_out' };
    if (!input.smsReady)
        return { channel: null, to: null, band: 'no_sms_number' };
    return { channel: 'sms', to: mobile, band: null };
}
const lowerFirst = (text) => text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
/** *between 10:00 and 10:30 AM*, or *at 10:00 AM* for an exact time. */
export function arrivalWords(start, window) {
    return window > 0 ? betweenWords(start, start + window) : `at ${clock(start)}`;
}
/** The text (Board 7, Board 10 C). */
export function confirmationText(facts) {
    const call = facts.phone ? `call ${facts.phone}` : 'call us';
    return `Hi ${facts.firstName} — this is ${facts.company}. ${facts.salesperson} will come to `
        + `${facts.streetLine} on ${dayComma(facts.date)} ${arrivalWords(facts.start, facts.arrivalMinutes)} `
        + `to look at your ${lowerFirst(facts.jobTitle)} (about ${minutesWords(facts.minutes)}). `
        + `Reply C to confirm, or ${call} to change it. Reply STOP to opt out.`;
}
/** The email (Board 9 B) — its date written out in full, the one exception to §3. */
export function confirmationEmail(facts) {
    const arriving = facts.arrivalMinutes > 0
        ? `arriving ${betweenWords(facts.start, facts.start + facts.arrivalMinutes)}`
        : `arriving at ${clock(facts.start)}`;
    const call = facts.phone ? `call ${facts.phone}` : 'call us';
    return {
        subject: `Your site visit with ${facts.salesperson} — ${dayComma(facts.date)}`,
        lines: [
            `Hi ${facts.firstName}, thanks for reaching out to ${facts.company}. ${facts.salesperson} `
                + `will come to ${facts.streetLine} on ${dayLong(facts.date)}, ${arriving}, to look at your `
                + `${lowerFirst(facts.jobTitle)}. It takes about ${minutesWords(facts.minutes)} — it helps if `
                + 'whoever is deciding can be there.',
            facts.salesperson,
            [facts.salespersonTitle, facts.phone].filter(Boolean).join(' · '),
            `Need another time? Reply to this email or ${call}.`,
        ],
    };
}
/** The one reply Rowted sends, to a *C* (§12.4). */
export const confirmedReply = (firstName, date) => `Thanks, ${firstName} — you’re confirmed for ${weekdayLong(date)}. See you then.`;
/** What counts as confirming: *C*, *Yes*, *Y*, *Confirm*, in any case. */
export const isConfirmReply = (body) => /^(c|yes|y|confirm)[.!]?$/i.test(body.trim());
/** Twilio's opt-out words, and a plain-language request to stop (§12.3). */
export function isStopReply(body) {
    const said = body.trim().toLowerCase();
    if (/^(stop|stopall|unsubscribe|cancel|end|quit|revoke|optout|opt out)[.!]?$/.test(said))
        return true;
    return /\b(stop|quit) (texting|messaging) me\b|\bno more (texts|messages)\b|\bdon[’']?t text me\b/.test(said);
}
//# sourceMappingURL=confirm.js.map